import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const JourneyTimeline = () => {
  const [activePhase, setActivePhase] = useState(0);

  const journeyPhases = [
    {
      id: 0,
      year: "2021",
      title: "The Spark",
      subtitle: "First Lines of Code",
      description: `My journey began with a simple "Hello World" in Python. What started as curiosity quickly became obsession. Late nights turned into early mornings as I discovered the magic of bringing ideas to life through code.`,
      details: `During this foundational period, I immersed myself in online tutorials, coding challenges, and small projects. The thrill of solving problems and seeing immediate results fueled my passion. I built my first calculator app and felt like I could conquer the world.`,
      icon: "Zap",
      color: "from-accent to-primary",
      image: "/me.png",
      alt :"Professional headshot of Ebesoh Adrian, Developer, Tutor, and Tech Innovator",
      achievements: [
        "First Python program",
        "100+ coding challenges solved",
        "Built first web application"
      ]
    },
    {
      id: 1,
      year: "2022-2023",
      title: "The Foundation",
      subtitle: "Formal Learning & Growth",
      description: `Enrolled in computer science courses and joined coding bootcamps. This period was about building solid fundamentals while exploring different technologies and finding my niche in web development.`,
      details: `I dove deep into JavaScript, React, and backend technologies. Participated in hackathons, contributed to open source projects, and started building a portfolio. Each project taught me something new about both coding and problem-solving.`,
      icon: "BookOpen",
      color: "from-primary to-secondary",
      image: "/images/about/me1.jpg",
      achievements: [
        "Completed Full-Stack Bootcamp",
        "First hackathon participation",
        "10+ portfolio projects"
      ]
    },
    {
      id: 2,
      year: "2025",
      title: "Professional Breakthrough",
      subtitle: "Joining Diotal",
      description: `Landed my first professional role at Diotal, where I contributed to enterprise-level applications. This experience taught me about scalable architecture, team collaboration, and the business side of technology.`,
      details: `At Diotal, I worked on critical projects that served thousands of users. I learned about code reviews, testing, deployment pipelines, and most importantly, how to write maintainable code. My contributions led to a 40% improvement in application performance.`,
      icon: "Briefcase",
      color: "from-secondary to-accent",
      image: "/images/about/me2.jpg",
      alt: "Professional headshot of Ebesoh Adrian, Developer, Tutor, and Tech Innovator",
      achievements: [
        "40% performance improvement",
        "Led major feature releases",
        "Mentored junior developers"
      ]
    },
    {
      id: 3,
      year: "2024-2025",
      title: "Community Builder",
      subtitle: "Founding CodersHub",
      description: `Recognized the need for a supportive learning community in Cameroon. Founded CodersHub to create a space where aspiring developers could learn, grow, and support each other on their coding journey.`,
      details: `CodersHub started as a small WhatsApp group and grew into a thriving community of over 500 members. We organized workshops, coding challenges, and mentorship programs. Seeing members land their first tech jobs became my greatest motivation.`,
      icon: "Users",
      color: "from-accent to-success",
      image: "/images/about/me3.jpg",
      alt :"Professional headshot of Ebesoh Adrian, Developer, Tutor, and Tech Innovator",
      achievements: [
        "100+ community members",
        "10+ workshops conducted",
        "2+ job placements facilitated"
      ]
    },
    {
      id: 4,
      year: "2024-2025",
      title: "Mentorship Impact",
      subtitle: "IMAC Program Leadership",
      description: `Joined the IMAC mentorship program, focusing on structured guidance for emerging developers. This role allowed me to refine my teaching methodology and create systematic learning paths.`,
      details: `Through IMAC, I developed a comprehensive mentorship framework that combines technical skills with soft skills development. My mentees have gone on to secure positions at top tech companies, with an 85% job placement rate.`,
      icon: "Award",
      color: "from-success to-primary",
      image: "/images/about/me2.jpg",
      alt :"Professional Full photo of Ebesoh Adrian, Developer, Tutor, and Tech Innovator",
      achievements: [
        "85% mentee job placement rate",
        "Developed mentorship framework",
        "Recognized as top mentor"
      ]
    },
    {
      id: 5,
      year: "2025-Present",
      title: "Thought Leadership",
      subtitle: "Scaling Impact",
      description: `Today, I focus on scaling my impact through speaking engagements, content creation, and strategic partnerships. My mission is to bridge the gap between individual growth and community development.`,
      details: `Currently working on multiple initiatives including a developer education platform, international mentorship programs, and tech community partnerships across Africa. The goal is to create sustainable pathways for the next generation of African developers.`,
      icon: "Rocket",
      color: "from-primary to-accent",
      image: "/images/about/me4.jpg",
      alt: "Professional headshot of Ebesoh Adrian, Developer, Tutor, and Tech Innovator",
      achievements: [
        "10+ speaking engagements",
        "National partnerships",
        "Educational platform in development"
      ]
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-12 h-1 bg-primary rounded-full"></div>
            <span className="text-primary font-medium tracking-wider uppercase text-sm">
              My Journey
            </span>
            <div className="w-12 h-1 bg-primary rounded-full"></div>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-secondary mb-6">
            From Curiosity to
            <span className="text-gradient-gold"> Impact</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Every milestone shaped not just my skills, but my understanding of how technology can transform lives and communities.
          </p>
        </div>

        {/* Timeline Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {journeyPhases?.map((phase, index) => (
            <button
              key={phase?.id}
              onClick={() => setActivePhase(index)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                activePhase === index
                  ? 'bg-primary text-white shadow-lg'
                  : 'bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary'
              }`}
            >
              {phase?.year}
            </button>
          ))}
        </div>

        {/* Active Phase Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8 animate-fade-in">
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${journeyPhases?.[activePhase]?.color} flex items-center justify-center shadow-lg`}>
                  <Icon name={journeyPhases?.[activePhase]?.icon} size={28} className="text-white" />
                </div>
                <div>
                  <div className="text-sm text-primary font-medium mb-1">
                    {journeyPhases?.[activePhase]?.year}
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-secondary">
                    {journeyPhases?.[activePhase]?.title}
                  </h3>
                  <p className="text-lg text-muted-foreground">
                    {journeyPhases?.[activePhase]?.subtitle}
                  </p>
                </div>
              </div>

              <p className="text-lg text-foreground leading-relaxed">
                {journeyPhases?.[activePhase]?.description}
              </p>

              <p className="text-muted-foreground leading-relaxed">
                {journeyPhases?.[activePhase]?.details}
              </p>
            </div>

            {/* Achievements */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-secondary">Key Achievements</h4>
              <div className="space-y-3">
                {journeyPhases?.[activePhase]?.achievements?.map((achievement, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-success rounded-full"></div>
                    <span className="text-foreground">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center space-x-4 pt-4">
              <button
                onClick={() => setActivePhase(Math.max(0, activePhase - 1))}
                disabled={activePhase === 0}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="ChevronLeft" size={16} />
                <span>Previous</span>
              </button>
              <button
                onClick={() => setActivePhase(Math.min(journeyPhases?.length - 1, activePhase + 1))}
                disabled={activePhase === journeyPhases?.length - 1}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>Next</span>
                <Icon name="ChevronRight" size={16} />
              </button>
            </div>
          </div>

          {/* Image */}
          <div className="relative animate-slide-up">
            <div className="relative rounded-2xl overflow-hidden shadow-elevated">
              <Image
                src={journeyPhases?.[activePhase]?.image}
                alt={`${journeyPhases?.[activePhase]?.title} phase`}
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/40 via-transparent to-transparent"></div>
              
              {/* Overlay Content */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${journeyPhases?.[activePhase]?.color} flex items-center justify-center`}>
                      <Icon name={journeyPhases?.[activePhase]?.icon} size={16} className="text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-secondary">
                        {journeyPhases?.[activePhase]?.title}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {journeyPhases?.[activePhase]?.year}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2">
              <div className="flex space-x-2">
                {journeyPhases?.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === activePhase ? 'bg-primary w-8' : 'bg-border'
                    }`}
                  ></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JourneyTimeline;